import {
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addRef,
  addState,
  addWrapper,
  flowMax,
} from 'ad-hok'
import { ChangeEvent, FC } from 'react'
import cx from 'classnames'
import { createUseStyles } from 'react-jss'
import { typedAs } from 'shared/src/utils/typedAs'
import {
  addDebouncedCopy,
  addEffectOnMount,
  addExtendedHandlers,
  addPropTrackingRef,
} from 'ad-hok-utils'
import {
  addNavigate,
  addOptionalIntPathParam,
} from 'shared/src/utils/addNavigate'
import { addApi } from 'shared/src/api'
import { ProductWithImageAndRelatedProductIds } from 'shared/src/api/types'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Modal } from '../Modal'
import { IconButton } from '../IconButton'
import { BODY_BOLD_STYLE, BODY_MEDIUM_STYLE, NOTES_BOLD_STYLE } from '../styles'
import { Product } from './Product'
import { CreateNewButton } from '../CreateNewButton'
import { makeCreatePostProductRoute } from '../../utils/routes'

const SIDE_PADDING = 32
const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
    paddingLeft: SIDE_PADDING,
    paddingRight: SIDE_PADDING,
    paddingTop: 76,
    height: '100dvh',
  },
  header: {
    display: 'flex',
    gap: 8,
    marginBottom: 24,
    height: 26,
  },
  input: {
    ...BODY_MEDIUM_STYLE,
    flexGrow: 1,
    caretColor: '#90734F',
    outline: 'none',
    color: 'rgba(54, 52, 50, 0.96)',

    '&::placeholder': {
      color: 'rgba(144, 115, 79, 0.56)',
      opacity: 1,
    },
  },
  closeButton: {
    position: 'relative',
    right: -10,
    alignSelf: 'center',
  },
  closeButtonIcon: {
    color: '#90734F',
  },
  promptContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  prompt: {
    ...BODY_BOLD_STYLE,
    maxWidth: 190,
    marginBottom: 24,
    marginLeft: 4,
    color: '#CBA67A',
  },
  createNewButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: 64,
    borderRadius: 2,
    backgroundColor: 'rgba(54, 52, 50, 0.02)',
    paddingLeft: 20,
    paddingRight: 24,
    marginBottom: 31,
    flexShrink: 0,
  },
  createNewButtonText: {
    ...NOTES_BOLD_STYLE,
    color: '#90734F',
    marginLeft: 28,
    marginRight: 'auto',
  },
  createNewButtonChevronRightIcon: {
    color: '#CBA67A',
  },
  searchResultContainer: {
    backgroundColor: 'transparent',
    flexShrink: 0,
    paddingLeft: SIDE_PADDING,
    paddingRight: SIDE_PADDING,
  },
  searchResultsContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    paddingBottom: 24,
    alignSelf: 'center',
    width: `calc(100% + ${2 * SIDE_PADDING}px)`,
  },
})

interface CreateNewProductButtonProps {
  name?: string
}

const CreateNewProductButton: FC<CreateNewProductButtonProps> = flowMax(
  addDisplayName('CreateNewProductButton'),
  addNavigate,
  addOptionalIntPathParam('userId'),
  addHandlers({
    onClick:
      ({ navigate, userId, name }) =>
      () => {
        navigate(makeCreatePostProductRoute(userId), {
          state: {
            name,
          },
        })
      },
  }),
  addTranslationHelpers,
  addProps(({ name, t }) => ({
    label: name
      ? t('admin.post.createNewName', { name })
      : t('admin.post.createNew'),
  })),
  ({ label, onClick }) => <CreateNewButton label={label} onClick={onClick} />
)

interface SearchResultsProps {
  searchResults: ProductWithImageAndRelatedProductIds[]
  onProductChoose: (productId: number) => void
  onTouchStart: () => void
}

const SearchResults: FC<SearchResultsProps> = flowMax(
  addDisplayName('SearchResults'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ searchResults, onProductChoose, onTouchStart, classes }) => (
    <div onTouchStart={onTouchStart} className={classes.searchResultsContainer}>
      {searchResults.map((searchResult) => (
        <Product
          product={searchResult}
          onClick={() => onProductChoose(searchResult.id)}
          className={classes.searchResultContainer}
          key={searchResult.id}
        />
      ))}
    </div>
  )
)

interface Props {
  onClose: () => void
  searchText: string
  setSearchText: (searchText: string) => void
  alreadySelectedProductIds: number[]
  onProductChoose: (productId: number) => void
}

export const SearchModal: FC<Props> = flowMax(
  addDisplayName('SearchModal'),
  addHandlers({
    onChangeSearchText:
      ({ setSearchText }) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value)
      },
  }),
  addExtendedHandlers({
    onClose:
      ({ setSearchText }) =>
      () => {
        setSearchText('')
      },
  }),
  addRef('inputRef', typedAs<HTMLInputElement | null>(null)),
  addHandlers(
    {
      focusInput:
        ({ inputRef }) =>
        () => {
          inputRef.current?.focus()
        },
      blurInputIfFocused:
        ({ inputRef }) =>
        () => {
          if (!inputRef.current) return
          if (window.document.activeElement !== inputRef.current) return
          inputRef.current.blur()
        },
    },
    ['inputRef']
  ),
  addEffectOnMount(({ focusInput }) => () => {
    focusInput()
  }),
  addState(
    'searchResults',
    'setSearchResults',
    typedAs<ProductWithImageAndRelatedProductIds[] | null>(null)
  ),
  addDebouncedCopy(200, 'searchText', 'searchTextDebounced'),
  addApi,
  addPropTrackingRef('searchText', 'searchTextRef'),
  addState('isLoadingSearchResults', 'setIsLoadingSearchResults', false),
  addEffect(
    ({
        searchTextDebounced,
        api,
        searchTextRef,
        setSearchResults,
        alreadySelectedProductIds,
        setIsLoadingSearchResults,
      }) =>
      () => {
        setIsLoadingSearchResults(true)
        api.products.searchUsedByUser!(searchTextDebounced).then((products) => {
          setIsLoadingSearchResults(false)
          if (searchTextRef.current !== searchTextDebounced) return

          setSearchResults(
            products.filter(
              ({ id }) =>
                !alreadySelectedProductIds.find((productId) => productId === id)
            )
          )
        })
      },
    ['searchTextDebounced']
  ),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  addWrapper(
    (
      render,
      {
        focusInput,
        inputRef,
        onClose,
        searchText,
        onChangeSearchText,
        classes,
        t,
      }
    ) => (
      <Modal containerClassName={classes.container}>
        <div className={classes.header}>
          <IconButton name="search" onClick={focusInput} />
          <input
            placeholder={t('admin.post.searchProduct')}
            value={searchText}
            onChange={onChangeSearchText}
            ref={inputRef}
            className={cx(classes.input, 'focus:ring-0')}
          />
          <IconButton
            onClick={onClose}
            name="close"
            className={classes.closeButton}
            iconClassName={classes.closeButtonIcon}
            addHitSlop
          />
        </div>
        {render()}
      </Modal>
    )
  ),
  ({
    searchText,
    searchResults,
    onProductChoose,
    blurInputIfFocused,
    classes,
    t,
  }) => (
    <>
      {!searchText && (
        <div className={classes.promptContainer}>
          <div className={classes.prompt}>{t('admin.post.searchPrompt')}</div>
        </div>
      )}
      <CreateNewProductButton name={searchText || undefined} />
      <SearchResults
        searchResults={searchResults ?? []}
        onProductChoose={onProductChoose}
        onTouchStart={blurInputIfFocused}
      />
    </>
  )
)
